import React from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import decode from 'lean-he/decode';

import AddToFavorites from "./AddToFavorites";

import {absoluteFull} from "../../Styles/utilities";
import {PlayButton} from "./PlayButton";
import {event} from "../helpers";
import flag from "../../Feature/Feature";

const teaserWrapper = css`
  padding: 0 4px;
`;

const ShowTeaserHover = styled('div')`
  position: absolute;
  top: -12.5%;
  left: -12.5%;
  width: 125%;
  height: 125%;
  visibility: hidden;
  transform: scale(0.8);
  transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1), visibility 0s linear 400ms;
  box-shadow: 0 0 1px 0 #000;
  pointer-events: none;
`;

const ShowTeaserOverlay = styled('div')`
  ${absoluteFull()};
  right: 0;
  bottom: 0;
  opacity: 0;
  transform: translate(0, 0) scale(1.01);
  transition: opacity 400ms cubic-bezier(0.5, 0, 0.1, 1);
`;

const ShowTeaserExtras = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  transform: scale(0.9999);
  transition: opacity 250ms cubic-bezier(0.5, 0, 0.1, 1);
`;

const ShowTeaserInfo = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 4px;
  left: 0;
  width: 100%;
  padding: 8px;
  color: #fff;
  line-height: 1;
  white-space: normal;
  opacity: 0;
  transition: opacity 400ms cubic-bezier(0.5, 0, 0.1, 1);
  display: flex;
  flex-direction: column;
`;

const showTeaserWrapper = css`
  position: relative;
  padding: 0 4px;
  transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1);
  transform: translate(0, 0);
  cursor: pointer;

  &.start {
    ${ShowTeaserHover} {
      left: 0;
      transform-origin: left;
    }
  }

  &.end {
    ${ShowTeaserHover} {
      left: -25%;
      transform-origin: right;
    }
  }

  .parasol-container:not(.animating) & {
    &.hover {
      z-index: 4;
      
      ${ShowTeaserHover} {
        visibility: visible;
        transform: scale(0.999);
        transition: transform 400ms cubic-bezier(0.5, 0, 0.1, 1), visibility 400ms linear 0s;
        pointer-events: all;
      }

      & ${ShowTeaserOverlay} {
          opacity: 1;
          background: linear-gradient(to bottom, rgba(0,0,0,0) 0%,rgba(0,0,0,0.9) 85%);
        }
  
      ${ShowTeaserInfo} {
          opacity: 1;
          transition-delay: 250ms;
          transition-duration: 600ms;
        }
  
      .${ShowTeaserExtras} {
          opacity: 1;
        }
    }
  }
`;

const showTeaser = css`
  position: relative;
  padding-bottom: 150%;
  background: #111;
`;

const showTeaserLink = css`
  ${absoluteFull()};
  
  .home-rows-no-keyboard & {
    outline: none;
  }
`;

const showPoster = css`
  ${absoluteFull()};
  right: 0;
  bottom: 0;
  transform: scale(0.9999);
`;

const showTeaserPlayButton = css`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Hacks for better rendering in Chrome. Avoids shifting text
const chromeHack = `
  transform: translate(0, 0) scale(0.999);
  z-index: 5;
`;

const showTitle = css`
  ${chromeHack};
  margin-bottom: 4px;
  font-weight: 800;
  line-height: 1.25;
  text-transform: uppercase;
`;

const showDescription = css`
  line-height: 1.25;
`;

const showSeasons = css`
  margin-top: 4px;
  color: #05c789;
  font-weight: bold;
  text-transform: uppercase;
`;

const boundsClip = css`
  ${absoluteFull()};
  overflow: hidden;
`;


function isNumber(n: ?number): boolean {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export class ShowPlaceholder extends React.PureComponent {
  render() {
    return <div css={teaserWrapper}>
      <div css={showTeaser}/>
    </div>;
  }
}


function trackEvent(label: string): string => void {
  return function(): void {
    event('ClickShow')(label);
  }
}

export default class ShowTeaser extends React.PureComponent {
  className() {
    let { hoverPos, viewPosition, pageSize } = this.props;
    let className = '';

    if (viewPosition !== null && viewPosition !== undefined && isNumber(viewPosition)) {
      if (hoverPos === viewPosition) {
        className = `${className} hover`;
      }

      className = `${className} view-${viewPosition}`;

      if (viewPosition === 0) {
        className = `${className} start`;
      }

      if (viewPosition === pageSize - 1) {
        className = `${className} end`;
      }
    }

    return className;
  }

  style() {
    let { hoverPos, viewPosition, pageSize } = this.props;
    let styles = {};

    if (viewPosition !== null && viewPosition !== undefined && isNumber(viewPosition)) {
      if (hoverPos !== viewPosition && hoverPos !== null && hoverPos !== undefined && isNumber(hoverPos)) {
        let size = Math.floor(this.computeSize() * 0.25);

        if (hoverPos === 0) {
          styles.transform = `translate(${size}px, 0)`;
        } else if (hoverPos === pageSize - 1) {
          styles.transform = `translate(-${size}px, 0)`;
        } else if (viewPosition < hoverPos) {
          styles.transform = `translate(-${Math.floor(size / 2)}px, 0)`;
        } else if (viewPosition > hoverPos) {
          styles.transform = `translate(${Math.floor(size / 2)}px, 0)`;
        }
      }
    }

    return styles;
  }

  computeSize() {
    return (window.outerWidth * 0.92) / this.props.pageSize - 8;
  }

  showPoster() {
    let { poster, post: { post_title } } = this.props;

    if (poster && poster.search('pbs.org') !== -1) {
      poster = `${poster}.resize.360x540.jpg`;
    }

    poster = poster.replace(/^http:/, 'https:');

    return () => <img css={showPoster} src={poster} alt={post_title} />;
  }

  closePreview(e) {
    if (e.target === e.currentTarget && this.props.onClose) {
      this.props.onClose();
    }
  }

  renderPreviewClosed() {
    return <React.Fragment>
      <div css={boundsClip} key="poster-bounds"/>
      <ShowTeaserOverlay key="show-teaser-overlay" />
      <ShowTeaserInfo key="show-teaser-info"/>
      <ShowTeaserExtras key="show-teaser-extras"/>
    </React.Fragment>;
  }

  renderPreviewOpen() {

    // Naive sanitize
    let showDesc = this.props.post.post_content.replace(/<.*?>/g, '').substring(0, 100);
    showDesc = showDesc.substring(0, showDesc.lastIndexOf(' '));
    showDesc = decode(showDesc);

    let seasons =  this.props.seasons.filter(s => s.ordinal !== -1);
    let Poster = this.showPoster();

    return <React.Fragment>
      <div css={boundsClip} key="poster-bounds">
        <Poster/>
      </div>
      <ShowTeaserOverlay key="show-teaser-overlay" />
      <ShowTeaserInfo key="show-teaser-info" >
        <div css={showTeaserPlayButton}>
          <PlayButton/>
        </div>
        <div className="show-teaser-title">
          <div css={showTitle}>
            {decode(this.props.post.post_title)}
          </div>
          <div css={showDescription}>
            {showDesc} {showDesc && '...'}
          </div>
        </div>
        {
          seasons.length > 0 &&
          <div css={showSeasons} className="body-small">
            {seasons.length} season{seasons.length > 1 ? 's' : ''}
          </div>
        }
      </ShowTeaserInfo>
      <ShowTeaserExtras key="show-teaser-extras">
        { flag('favorites_list') &&
          <AddToFavorites
            isFav={this.props.favorite}
            add={() => this.props.addFavorite(this.props.post.ID)}
            del={() => this.props.removeFavorite(this.props.post.ID)}/> || null
        }
      </ShowTeaserExtras>
    </React.Fragment>;
  }

  getEventLabel(): string {
    let { post: { ID, post_title }, page, viewPosition, trackingLabelPrefix } = this.props;

    let trackingParts = [
      post_title,
      ID,
      page,
      viewPosition + 1
    ];

    if (trackingLabelPrefix) {
      trackingParts.unshift(trackingLabelPrefix);
    }

    return trackingParts.join(' | ');
  }

  render() {
    let { open, hoverPos, viewPosition } = this.props;
    let inView = viewPosition !== null;
    let hovered = hoverPos === viewPosition;

    let Poster = this.showPoster();

    return <div css={showTeaserWrapper} className={`show-teaser-wrapper ${this.className()}`} onMouseEnter={this.props.onMouseEnter} onMouseMove={this.props.onMouseEnter} onMouseLeave={this.props.onMouseLeave} style={this.style()}>
      <div css={showTeaser}>
        <a css={showTeaserLink} onClick={trackEvent(this.getEventLabel())} ref={this.props.innerRef} href={`/${this.props.post.post_name}/`} tabIndex={inView ? 0 : -1} aria-hidden={inView ? "false" : "true"} aria-label={this.props.post.post_title}>
          <Poster/>
          <ShowTeaserHover onTransitionEnd={open && !hovered ? this.closePreview.bind(this) : null}>
            {open ? this.renderPreviewOpen() : this.renderPreviewClosed()}
          </ShowTeaserHover>
        </a>
      </div>
    </div>;
  }
}